import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://cook-smart-40.emergent.host';
export const API_BASE = `${BACKEND_URL}/api`;

const SESSION_KEY = 'matbakhi_session_token';

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach Authorization header from localStorage so that auth survives
// Android WebView restarts where cookies may be wiped by the OS.
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(SESSION_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch { /* localStorage unavailable */ }
  return config;
});

export const saveSessionToken = (token) => {
  try { if (token) localStorage.setItem(SESSION_KEY, token); } catch { /* noop */ }
};
export const clearSessionToken = () => {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
};

// ────────────────────────────────────────────────────────────────────────
// In-memory GET cache (5-minute TTL)
// ────────────────────────────────────────────────────────────────────────
// Problem this solves: returning from a recipe detail back to a category
// page with 80+ recipes was re-fetching every recipe (slow on mobile data,
// and breaks ScrollRestoration because the page is empty when restoration
// fires). With this cache, navigating back is instant — `cachedGet` returns
// the cached array synchronously to the component on the next render.
//
// We intentionally cache ONLY safe, idempotent endpoints (recipes,
// categories, trending). User-specific data (favorites, profile) stays
// uncached so it always reflects the latest state.
const CACHE_TTL_MS = 5 * 60 * 1000;
const _cache = new Map();

function cacheKey(path, params) {
  return params ? `${path}?${JSON.stringify(params)}` : path;
}

/**
 * Drop-in replacement for `api.get(path, { params })` that caches the
 * response in memory for 5 minutes. The first call hits the network; all
 * subsequent calls inside the TTL return the cached value instantly.
 */
export async function cachedGet(path, options = {}) {
  const key = cacheKey(path, options.params);
  const entry = _cache.get(key);
  if (entry && Date.now() - entry.ts < CACHE_TTL_MS) {
    return { data: entry.data };
  }
  const res = await api.get(path, options);
  _cache.set(key, { data: res.data, ts: Date.now() });
  return res;
}

/** Invalidate one entry (or the entire cache) — call after a mutation. */
export function invalidateCache(pathPrefix) {
  if (!pathPrefix) { _cache.clear(); return; }
  for (const key of _cache.keys()) {
    if (key.startsWith(pathPrefix)) _cache.delete(key);
  }
}
